import { useCallback, useEffect, useState } from 'react';
import { ACCOUNT_SETTINGS_ROUTE, t, type PasskeyRow } from '@bms2/shared';
import { AlertBanner } from '../components/controls/AlertBanner';
import { apiFetch } from '../pages/records/api';
import { readSessionToken } from '../session';
import { PASSKEYS_PATH } from '../pages/settings/api';
import { PASSKEY_OFFER_DISMISSED_KEY, devicePreferenceStore, readPreference, writePreference } from './devicePreference';
import styles from './shell.module.css';

/**
 * 🔒 The offer that makes passkey enrolment discoverable (decision 0493), drawn
 * as a card at the top of the dashboard and a dot on the avatar menu's Account
 * Settings row, never as a strip over every page (decision 1442; T693).
 *
 * 💰 Enrolment has existed since decision 0351 and lives on the Account
 * settings page, where nobody found it: the product owner reported *"Still no
 * passkey prompt"* twice, on 2026-09-17 and again after looking. A capability
 * built on one side and unreachable from the other is the defect this plan
 * keeps finding, and here the unreachable half was simply a door nobody
 * pointed at.
 *
 * 🔨 IT IS AN OFFER, NEVER A REQUIREMENT. The authenticator stays the minimum
 * (decision 0351): a passkey is a faster second factor on a device somebody
 * already trusts, not a replacement for the code, and a card that blocked the
 * page would make it read as one.
 *
 * 🔨 A dismissal is remembered PER DEVICE, in this browser, because a passkey
 * IS per device. Storing it against the login would silence the offer on every
 * other machine the person signs in from — which is exactly where they have no
 * passkey yet.
 */

export { PASSKEY_OFFER_DISMISSED_KEY };

/** Whether this browser has been told not to ask again. A blocked or private store reads as "not dismissed". */
export function passkeyOfferDismissed(store: Pick<Storage, 'getItem'> | null = devicePreferenceStore()): boolean {
  return readPreference<boolean>(PASSKEY_OFFER_DISMISSED_KEY, (raw) => Boolean(raw), false, store);
}

export function dismissPasskeyOffer(store: Pick<Storage, 'setItem'> | null = devicePreferenceStore()): void {
  // A stored ISO day the offer compares against, not display text (T663).
  writePreference(PASSKEY_OFFER_DISMISSED_KEY, new Date().toISOString().slice(0, 10), store);
}

/** The frame's one answer to whether the offer stands, which the dashboard's card and the avatar menu's dot both read. */
export interface PasskeyOffer {
  offered: boolean;
  /** Set up a passkey: the offer closes on the way to the page that adds one. */
  accept: () => void;
  /** The close cross: this device is not asked again. */
  dismiss: () => void;
}

export interface UsePasskeyOfferOptions {
  apiBaseUrl?: string;
  /** Injected in tests; the real one reads the browser's own capability. */
  supported?: () => boolean;
  onOpenSettings?: () => void;
  /** The frame's own answer to whether a session is held; a marker it restores from the server asks the offer again (decision 3540). */
  sessionHeld?: boolean;
}

/**
 * 🔨 Asked once, by the frame, so the card and the dot never disagree and the
 * server is asked one question rather than two.
 */
export function usePasskeyOffer({ apiBaseUrl = '', supported, onOpenSettings, sessionHeld }: UsePasskeyOfferOptions = {}): PasskeyOffer {
  const [offered, setOffered] = useState(false);

  useEffect(() => {
    let live = true;
    const decide = async () => {
      // 🔨 Four questions, cheapest first, and the request is only made when the first three pass:
      // a signed-in session, a browser that can do this at all, and a device that has not said no.
      const able = supported ? supported() : typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
      if (!readSessionToken() || !able || passkeyOfferDismissed()) return;
      const response = await apiFetch(`${apiBaseUrl}${PASSKEYS_PATH}`).catch(/* silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server. */ () => null);
      if (!response?.ok) return;
      const body = (await response.json().catch(/* silent: a body that is not JSON reads as none, and the status check beside it answers the failure. */ () => null)) as { rows?: PasskeyRow[] } | PasskeyRow[] | null;
      const rows = Array.isArray(body) ? body : (body?.rows ?? null);
      // 🔨 An unreadable answer offers nothing. Guessing "probably none" would prompt somebody who already has one.
      if (!Array.isArray(rows)) return;
      if (live && rows.length === 0) setOffered(true);
    };
    void decide();
    return () => {
      live = false;
    };
  }, [apiBaseUrl, supported, sessionHeld]);

  const dismiss = useCallback(() => {
    dismissPasskeyOffer();
    setOffered(false);
  }, []);
  const accept = useCallback(() => {
    // 🔨 The offer closes on the way to the page that adds one, so finishing there does not
    // leave the card and the dot standing for a login that now has a passkey.
    dismissPasskeyOffer();
    setOffered(false);
    if (onOpenSettings) return onOpenSettings();
    window.location.assign(ACCOUNT_SETTINGS_ROUTE);
  }, [onOpenSettings]);

  return { offered, accept, dismiss };
}

/**
 * 🔒 The card at the top of the dashboard (decision 1442; T693): the offer's own
 * sentence, Set up a passkey and the close cross, drawn once and on the first
 * page a person lands on.
 */
export function PasskeyOfferCard({ offer }: { offer: PasskeyOffer | null | undefined }) {
  if (!offer?.offered) return null;
  return (
    <div className={styles.passkeyCard} data-passkey-offer="true">
      {/* 🔨 The neutral tone, never a warning: having no passkey is not a problem, and a tone that
          says it is would make an optional convenience read as something gone wrong. */}
      <AlertBanner tone="inactive" message={t('passkey.offer.body')} action={{ label: t('passkey.offer.add'), onAction: offer.accept }} onDismiss={offer.dismiss} />
    </div>
  );
}
